import { CostChart, type CostPoint } from "@/components/steel/cost-chart"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import { Input } from "@/components/ui/input"
import type { ProductionData, ProductionSection } from "@/pages/Projects/tabs/types"

const RATE_COLUMNS = ["Placed", "Hours", "Measured", "Assumed", "Bid", "% of plan", "Remaining hrs", "Override"]

function formatFinish(value?: string | null): string {
  if (!value) return "—"
  if (value === "unbounded") return "Unbounded"
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function formatRate(value?: number | null): string {
  if (value == null) return "—"
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ft²/hr`
}

function accessoryCaption(load?: number | null): string {
  return load == null || load === 1 ? "" : `Remaining hours include a ×${load.toFixed(2)} accessory load.`
}

function forecastSummary(p: ProductionData): string {
  const pace = p.hoursPerDay != null ? `${p.hoursPerDay.toLocaleString()} hrs/day` : "No pace yet"
  const days = p.remainingWorkDays != null ? ` · ${p.remainingWorkDays} work days left` : ""
  return `${pace}${days}${p.expectedFinish ? ` · finish ${formatFinish(p.expectedFinish)}` : ""}`
}

function rateCells(section: ProductionSection): string[] {
  return [
    `${section.completedSqft.toLocaleString()} / ${section.totalSqft.toLocaleString()}`, section.hours.toLocaleString(),
    formatRate(section.measuredRate), formatRate(section.assumedRate), formatRate(section.bidRate),
    section.pctOfPlan != null ? `${Math.round(section.pctOfPlan)}%` : "—", section.remainingHours.toLocaleString(),
  ]
}

function rateChartPoints(section: ProductionSection): CostPoint[] {
  return section.rateSeries.map((p) => ({
    label: new Date(`${p.label}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    planned: p.bidRate,
    actual: p.measuredRate,
  }))
}

export function RateBySection({
  overrides,
  production,
  saveOverride,
  sections,
  setOverrides,
}: {
  overrides: Record<string, string>
  production: ProductionData
  saveOverride: (key: "steel" | "wall" | "roof") => void
  sections: ProductionSection[]
  setOverrides: (updater: (o: Record<string, string>) => Record<string, string>) => void
}) {
  const loadCaption = accessoryCaption(production.accessoryLoad)

  return (
    <div className="mt-6">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-title font-semibold">Rate by section</h2>
        <p className="text-meta text-muted-foreground">
          {forecastSummary(production)}
          {loadCaption ? <span className="block">{loadCaption}</span> : null}
        </p>
      </div>
      <DataTable minWidth={680}>
        <Thead>
          <Th>Section</Th>
          {RATE_COLUMNS.map((column) => (
            <Th key={column} align="right">{column}</Th>
          ))}
        </Thead>
        <tbody>
          {sections.map((s) => (
            <Tr key={s.key}>
              <Td strong>{s.label}</Td>
              {rateCells(s).map((cell, index) => (
                <Td key={index} align="right" num muted={index !== 2 && index !== 3}>{cell}</Td>
              ))}
              <Td align="right">
                <Input
                  className="num ml-auto h-7 w-24" type="number" min="0" placeholder="—"
                  value={overrides[s.key] ?? ""} onBlur={() => saveOverride(s.key)}
                  onChange={(e) => setOverrides((o) => ({ ...o, [s.key]: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && saveOverride(s.key)} />
              </Td>
            </Tr>
          ))}
        </tbody>
      </DataTable>

      <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {sections.map((s) => (
          <div key={s.key}>
            <h3 className="mb-2 text-label font-medium">{s.label} — ft²/man-hour</h3>
            <CostChart points={rateChartPoints(s)} height={160} bands={3} />
          </div>
        ))}
      </div>
    </div>
  )
}
